"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import type React from "react";
import {
  type DefaultValues,
  type FieldValues,
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
  useForm,
} from "react-hook-form";
import type { z } from "zod";
import { cn } from "@/lib/utils";

export interface FormProps<T extends FieldValues> {
  // biome-ignore lint/suspicious/noExplicitAny: <any> is used to allow for any type of schema
  schema: z.ZodType<T, FieldValues, any>;
  onSubmit: SubmitHandler<T>;
  defaultValues?: Partial<T>;
  mode?: "onSubmit" | "onBlur" | "onChange" | "onTouched" | "all" | undefined;
  reValidateMode?: "onSubmit" | "onBlur" | "onChange" | undefined;
  children: React.ReactNode | ((form: UseFormReturn<T>) => React.ReactNode);
  className?: string;
  formProps?: Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit">;
}

export const Form = <T extends FieldValues>({
  schema,
  onSubmit,
  defaultValues,
  mode = "onSubmit",
  reValidateMode = "onChange",
  children,
  className,
  formProps = {},
}: FormProps<T>) => {
  const form = useForm<T>({
    resolver: zodResolver(schema),
    mode,
    reValidateMode,
    defaultValues: defaultValues as DefaultValues<T>,
  });

  const renderChildren = () => {
    if (typeof children === "function") {
      return children(form);
    }
    return children;
  };

  return (
    <FormProvider {...form}>
      {/* noValidate: this form validates through RHF + Zod, so the browser's
          native constraint popups (e.g. type="url" → "Please enter a URL.")
          must not fire and pre-empt our own field errors. */}
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn("space-y-4", className)}
        {...formProps}
      >
        {renderChildren()}
      </form>
    </FormProvider>
  );
};
